import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Toolbar,
  IconButton,
  Tooltip,
  Box,
  Divider,
  Collapse,
  ListSubheader,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  KeyboardTab as KeyboardTabSharpIcon,
  Storefront as StorefrontIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Loyalty as LoyaltyIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Badge from '@mui/material/Badge';
import { useOrderNotifications } from '../context/OrderNotificationContext';
import ReorderIcon from '@mui/icons-material/Reorder';
import BusinessIcon from '@mui/icons-material/Business';
interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  drawerWidth: number;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen, drawerWidth }) => {
  const navigate = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { unreadCount, resetUnread } = useOrderNotifications();

  console.log('Rendering Sidebar with unreadCount:', unreadCount);

  const settingsItems = [
    { text: 'Loyalty', path: '/settings/loyalty', icon: LoyaltyIcon },
    { text: 'Store Credit', path: '/settings/store-credit', icon: CreditCardIcon },
    { text: 'Shipping Rates', path: '/settings/shipping', icon: LocalShippingIcon },
  ];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isOpen ? drawerWidth : 72,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isOpen ? drawerWidth : 72,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          pt: 8,
          transition: 'width 0.3s ease',
          bgcolor: 'background.paper',
        },
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isOpen ? 'space-between' : 'center',
          px: 2,
        }}
      >
        {isOpen && <Box sx={{ fontWeight: 'bold' }}>Hide Menu</Box>}
        <IconButton onClick={() => setIsOpen(!isOpen)}>
          <KeyboardTabSharpIcon sx={{ transform: isOpen ? 'rotate(180deg)' : 'none' }} />
        </IconButton>
      </Toolbar>
      <Divider />

      <List>
  {[
    { text: 'Sell', path: '/sell', icon: PointOfSaleIcon },
    { text: 'Dashboard', path: '/dashboard', icon: DashboardIcon },
    { text: 'Products', path: '/products', icon: InventoryIcon },
    { text: 'Users', path: '/users', icon: PeopleIcon },
    { text: 'Locations', path: '/locations', icon: StorefrontIcon },
    { text: 'Orders', path: '/orders', icon: ReceiptIcon },
  ].map((item) => {
    const isOrders = item.path === '/orders';
    const IconComponent = item.icon;

    return (
      <ListItem disablePadding key={item.text}>
        <Tooltip title={item.text} placement="right" disableHoverListener={isOpen}>
          <ListItemButton
            onClick={() => {
              navigate(item.path);
              if (isOrders) {
                console.log('Resetting unread count');
                resetUnread();
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isOpen ? 'flex-start' : 'center',
              px: 2,
              py: 1.5,
            }}
          >
            {isOrders ? (
              <Badge
                badgeContent={unreadCount > 0 ? unreadCount : null}
                color="error"
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                  '& .MuiBadge-badge': {
                    right: isOpen ? 15 : 0,
                    top: 4,
                  },
                }}
              >
                <IconComponent
                  sx={{
                    mr: isOpen ? 2 : 0,
                    fontSize: '1.25rem',
                    position: 'relative',
                  }}
                />
              </Badge>
            ) : (
              <IconComponent sx={{ mr: isOpen ? 2 : 0, fontSize: '1.25rem' }} />
            )}
            {isOpen && <ListItemText primary={item.text} />}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  })}

  {/* --- Stock Control Group --- */}
  {isOpen && (
    <ListSubheader
      sx={{
        bgcolor: 'transparent',
        fontWeight: 'bold',
        fontSize: '0.875rem',
        lineHeight: 'normal',
        py: 1,
        pl: 2,
      }}
    >
      Stock Control
    </ListSubheader>
  )}

  {[
    { text: 'Receive Stock', path: '/stock-receive', icon: ShoppingBasketIcon },
    { text: 'Receipts', path: '/stock-receipts', icon: HistoryIcon },
    { text: 'Reorder', path: '/reorder-list', icon: ReorderIcon },
        { text: 'Suppliers', path: '/supplier-list', icon: BusinessIcon },

  ].map((item) => {
    const IconComponent = item.icon;
    return (
      <ListItem disablePadding key={item.text}>
        <Tooltip title={item.text} placement="right" disableHoverListener={isOpen}>
          <ListItemButton
            onClick={() => navigate(item.path)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isOpen ? 'flex-start' : 'center',
              px: 2,
              py: 1.5,
            }}
          >
            <IconComponent sx={{ mr: isOpen ? 2 : 0, fontSize: '1.25rem' }} />
            {isOpen && <ListItemText primary={item.text} />}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  })}
</List>


      <Divider />

      <List
        subheader={
          isOpen && (
            <ListSubheader
              sx={{
                bgcolor: 'transparent',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                lineHeight: 'normal',
                py: 1,
              }}
            >
              Settings
            </ListSubheader>
          )
        }
      >
        <ListItem disablePadding>
          <Tooltip title="Settings" placement="right" disableHoverListener={isOpen}>
            <ListItemButton
              onClick={() => {
                if (isOpen) {
                  setIsSettingsOpen(!isSettingsOpen);
                } else {
                  navigate('/settings');
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isOpen ? 'flex-start' : 'center',
                px: 2,
                py: 1.5,
              }}
            >
              <SettingsIcon sx={{ mr: isOpen ? 2 : 0, fontSize: '1.25rem' }} />
              {isOpen && <ListItemText primary="Settings" />}
              {isOpen && (isSettingsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        <Collapse in={isSettingsOpen && isOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {settingsItems.map((item) => (
              <ListItem disablePadding key={item.text}>
                <Tooltip title={item.text} placement="right" disableHoverListener={isOpen}>
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      pl: isOpen ? 4 : 2,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <item.icon sx={{
                      mr: isOpen ? 2 : 0,
                      fontSize: '1rem',
                      color: 'text.secondary'
                    }} />
                    {isOpen && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </List>
    </Drawer>
  );
};

export default Sidebar;